import React, { useEffect, useState } from 'react';
import axios from '../../axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#f0f2f5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(5),
  fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
}));

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 600,
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[6],
  backgroundColor: '#ffffff',
}));

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  borderBottom: `1px solid ${theme.palette.divider}`,
  textAlign: 'center',
}));

const Content = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(4),
}));

const ButtonGroup = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(4),
  justifyContent: 'center',
  gap: theme.spacing(3),

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchCurrentUser() {
      try {
        const res = await axios.get('/getAdminProfile.php', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        console.log('API response:', res.data);
        setEmail(res.data.email);
      } catch (err) {
  console.error('Error fetching profile:', err.response ? err.response.data : err.message);
  setError('Failed to load current email.');
}
 finally {
        setLoading(false);
      }
    }
    fetchCurrentUser();
  }, []);

  if (loading) {
    return (
      <PageWrapper>
        <CircularProgress />
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <Alert severity="error">{error}</Alert>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <StyledCard>
        <Header>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#222' }}>
            Admin Dashboard
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 1, color: '#666' }}>
            Welcome back, <strong>{email}</strong>
          </Typography>
        </Header>

        <Content>
          <Typography variant="body1" sx={{ color: '#444', mb: 2, lineHeight: 1.6 }}>
            Manage your admin profile and create new administrators here.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <ButtonGroup direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate('/admin/update-email')}
              sx={{
                minWidth: 160,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': { backgroundColor: '#e3f2fd' },
              }}
            >
              Change Email
            </Button>

            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate('/admin/update-credentials')}
              sx={{
                minWidth: 160,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': { backgroundColor: '#e3f2fd' },
              }}
            >
              Change Password
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/admin/create')}
              sx={{
                minWidth: 160,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
              }}
            >
              Create Admin
            </Button>
          </ButtonGroup>
        </Content>
      </StyledCard>
    </PageWrapper>
  );
};

export default AdminDashboard;
